import { motion } from 'framer-motion';
import { useData } from 'vike-react/useData';
import { getAudienceById } from '@shared/constants/audiences';
import { Container } from '../../components/layout/Container';
import { Reveal } from '../../components/motion/Reveal';
import { StaggerGrid, StaggerItem } from '../../components/motion/StaggerGrid';
import { CountUp } from '../../components/motion/CountUp';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';
import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';
import { ConcreteCase } from '../../components/blocks/ConcreteCase';
import { SectorHero } from '../../components/sector/SectorHero';
import { SectorProof } from '../../components/sector/SectorProof';
import { SectorFAQ } from '../../components/sector/SectorFAQ';
import { SectorCTA } from '../../components/sector/SectorCTA';
import { getAudienceVoice } from '../../config/audience-voice';
import { audienceLegacyTone } from '../../config/audience-theme';
import { easeStripe } from '../../lib/motion';
import type { Data } from './+data';

const AUDIENCE_ID = 'agriculteurs' as const;

const AGRI_AIDES: Array<{
  label: string;
  sub: string;
  color: 'brand' | 'accent' | 'lavender' | 'sage';
}> = [
  {
    label: 'Primes énergie (CEE) agricoles',
    sub: "Fiches AGRI : pompe à chaleur, régulation d'élevage, éclairage LED de hangar, isolation de toiture.",
    color: 'accent',
  },
  {
    label: "PCAE (Plan de compétitivité et d'adaptation des exploitations) régional",
    sub: 'Plan de compétitivité instruit par votre Région — taux et plafonds variables selon le territoire.',
    color: 'brand',
  },
  {
    label: 'FranceAgriMer',
    sub: "Aides nationales à la modernisation et aux investissements de votre exploitation.",
    color: 'lavender',
  },
  {
    label: 'Photovoltaïque',
    sub: 'Ombrières, toiture de hangar, autoconsommation ou revente — cumulable avec les primes énergie (CEE) agricoles.',
    color: 'sage',
  },
];

export default function AgriculteurPage() {
  const { journey, tools, cases } = useData<Data>();
  const audience = getAudienceById(AUDIENCE_ID)!;
  const voice = getAudienceVoice(AUDIENCE_ID);
  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Agriculteurs', url: 'https://solutionecofrance.fr/agriculteurs' },
        ]}
      />

      <SectorHero
        audience={audience}
        voice={voice}
        sideSlot={<AgriculteurPreview />}
      />

      <SectorProof audience={audience} voice={voice} variant="bento" />

      <div id="methode" />
      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {/* AIDES CUMULABLES — spécifique exploitations agricoles */}
      <section className="bg-stone-50/50 py-16 md:py-20">
        <Container>
          <Reveal>
            <div className="mx-auto mb-10 max-w-xl text-center">
              <p className="eyebrow justify-center">Aides cumulables exploitation</p>
              <h2 className="mt-3 h-section">Quatre leviers à cumuler sur vos bâtiments.</h2>
            </div>
          </Reveal>

          <StaggerGrid className="mx-auto grid max-w-5xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {AGRI_AIDES.map((a) => (
              <StaggerItem
                key={a.label}
                className="rounded-xl border border-stone-200/70 bg-paper p-5 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:shadow-elevated"
              >
                <div
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-sm font-semibold ${
                    a.color === 'brand'
                      ? 'bg-brand-50 text-brand-700'
                      : a.color === 'accent'
                        ? 'bg-accent-50 text-accent-800'
                        : a.color === 'lavender'
                          ? 'bg-lavender-50 text-lavender-700'
                          : 'bg-sage-100 text-sage-500'
                  }`}
                >
                  €
                </div>
                <p className="mt-3 h-card">{a.label}</p>
                <p className="mt-1 text-[11px] text-stone-500 leading-relaxed">{a.sub}</p>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </Container>
      </section>

      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,
            description: t.description,
            iconName: t.iconName,
            href: t.href,
            badge: t.badge,
            metric: t.metric,
            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      <SectorFAQ audience={audience} voice={voice} />

      <SectorCTA audience={audience} voice={voice} id="diagnostic-agri" />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// AgriculteurPreview — side card hero
// Aperçu d'un dossier CEE secteur AGRI en cours (hangar laitier).
// ════════════════════════════════════════════════════════════════

function AgriculteurPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
      className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-accent-200/60 md:p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Dossier primes énergie (CEE) secteur agricole (AGRI)
          </p>
          <p className="mt-0.5 text-xs text-stone-500">Ferme laitière · Calvados</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-md bg-accent-50 px-2 py-0.5 text-[10px] font-semibold text-accent-700 ring-1 ring-inset ring-accent-200/50">
          <span className="h-1 w-1 rounded-full bg-accent-500 animate-glow-pulse" />
          En cours
        </span>
      </div>

      <div className="mt-5 flex items-baseline gap-2 font-display tracking-tight">
        <span className="text-3xl font-bold text-ink md:text-4xl">
          <CountUp end={42000} />
        </span>
        <span className="text-base text-stone-500">€ d'aides estimées</span>
      </div>

      <div className="mt-5 space-y-1.5">
        {[
          {
            label: 'Prime CEE · pompe à chaleur bâtiment agricole',
            val: '18 400 €',
            status: 'Validé',
            tone: 'brand',
          },
          {
            label: 'Prime CEE · isolation des parois',
            val: '12 200 €',
            status: 'Instruction',
            tone: 'accent',
          },
          {
            label: 'PCAE (aide régionale) — Région',
            val: '11 400 €',
            status: 'Dossier',
            tone: 'lavender',
          },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-stone-700">{row.label}</p>
              <p
                className={`mt-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  row.tone === 'brand'
                    ? 'text-brand-700'
                    : row.tone === 'lavender'
                      ? 'text-lavender-700'
                      : 'text-accent-800'
                }`}
              >
                {row.status}
              </p>
            </div>
            <span className="ml-2 font-mono text-xs font-semibold text-ink tabular-nums">
              {row.val}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Avance trésorerie</p>
        <p className="font-mono text-[12px] font-semibold text-accent-700 tabular-nums">
          0 €
        </p>
      </div>
    </motion.div>
  );
}
